<script setup>
const {
  component = "button",
  hasPadding = true,
  size = "md",
  variant = "primary",
  gray = false,
  destructive = false,
  isLoading = false,
  customClasses = ""
} = defineProps({
  component: { type: String, required: false },
  hasPadding: { type: Boolean, required: false },
  size: { type: String, required: false },
  destructive: { type: Boolean, required: false },
  isDisabled: { type: Boolean, required: false },
  isLoading: { type: Boolean, required: false },
  variant: { type: String, required: false },
  gray: { type: Boolean, required: false },
  customClasses: { type: String, required: false }
});
</script>

<template>
    <BaseButton
        :disabled="isDisabled"
        v-bind="{
  ...$props,
  component,
  isLoading,
  isDisabled
}"
        class="button"
        is-b2-b
        :size
        :custom-classes
        :class="[
  {
    'has-padding': hasPadding,
    'icon-only': $slots.icon && !$slots.default,
    destructive,
    gray,
    isLoading
  },
  variant,
  size
]"
    >
        <div
            v-if="isLoading"
            :class="{
  'size-3': size === 'sm',
  'size-4': size === 'md',
  'size-5': size === 'lg'
}"
        >
            <ChargemapSpinningLoader
                class="size-full"
                :class="{
  '!text-rose-500': destructive
}"
                :is-color-inverted="
  !isDisabled && variant === 'primary' && !destructive
"
            />
        </div>
        <template v-if="$slots.icon && !isLoading" #icon>
            <slot name="icon" />
        </template>
        <slot />
        <template v-if="$slots.iconRight" #iconRight>
            <slot name="iconRight" />
        </template>
    </BaseButton>
</template>

<style scoped>
.button {
  @apply rounded-lg outline-none transition ease-in-out duration-150;
}
.button.primary {
  @apply text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:bg-primary-700 focus:ring-primary-300/50 disabled:bg-slate-100 disabled:text-slate-300;
}
.button.primary.destructive {
  @apply text-white bg-rose-600 hover:bg-rose-700 focus:ring-4 focus:ring-rose-300/50 focus:border-rose-600 disabled:bg-slate-100 disabled:text-slate-300;
}
.button.primary.destructive.isLoading {
  @apply ring-0;
}
.button.primary.isLoading {
  @apply ring-0;
}
.button.secondary {
  @apply text-slate-500 bg-white border border-slate-300 hover:bg-slate-50 focus:ring-4 focus:ring-slate-200/50 disabled:text-slate-300 disabled:bg-slate-100;
}
.button.secondary.destructive {
  @apply text-rose-600 hover:bg-rose-50 border-rose-300 focus:ring-4 focus:ring-rose-200/50 disabled:bg-slate-100 disabled:text-slate-300 disabled:border-slate-300;
}
.button.secondary.destructive.isLoading {
  @apply ring-0;
}
.button.secondary.isLoading {
  @apply ring-0;
}
.button.tertiary {
  @apply text-primary-600 bg-transparent border-none hover:bg-primary-50 disabled:text-slate-300 focus:ring-slate-200/50 disabled:hover:bg-slate-50;
}
.button.tertiary.destructive {
  @apply text-rose-600 hover:bg-rose-50 disabled:text-slate-300 disabled:hover:bg-slate-50 focus:ring-rose-200/50;
}
.button.tertiary.destructive.isLoading {
  @apply ring-0;
}
.button.tertiary.gray {
  @apply text-slate-500 hover:bg-slate-50 disabled:text-slate-300;
}
.button.tertiary.isLoading {
  @apply ring-0;
}
.button.sm {
  @apply text-sm font-medium;
}
.button.sm.has-padding {
  @apply px-3 py-2;
}
.button.sm.has-padding.secondary {
  @apply px-3 py-[0.4375rem];
}
.button.sm.has-padding.secondary.icon-only {
  @apply p-[0.4375rem];
}
.button.sm.icon-only {
  @apply p-2;
}
.button.sm :slotted(svg) {
  @apply size-5 shrink-0 fill-current;
}
.button.md {
  @apply text-base font-medium;
}
.button.md.has-padding {
  @apply px-4 py-2.5;
}
.button.md.has-padding.secondary {
  @apply px-4 py-[0.5625rem];
}
.button.md.has-padding.secondary.icon-only {
  @apply p-[0.5625rem];
}
.button.md.icon-only {
  @apply p-3;
}
.button.md :slotted(svg) {
  @apply size-5 shrink-0 fill-current;
}
.button.lg {
  @apply text-base font-medium;
}
.button.lg.has-padding {
  @apply px-5 py-3;
}
.button.lg.has-padding.secondary {
  @apply px-5 py-[0.6875rem];
}
.button.lg.has-padding.secondary.icon-only {
  @apply p-[0.6875rem];
}
.button.lg.icon-only {
  @apply p-3;
}
.button.lg :slotted(svg) {
  @apply size-6 shrink-0 fill-current;
}
</style>
